import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { AuthProvider } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import Browse from "@/pages/Browse";
import ItemDetail from "@/pages/ItemDetail";
import CreateListing from "@/pages/CreateListing";
import EditListing from "@/pages/EditListing";
import Dashboard from "@/pages/Dashboard";
import Messages from "@/pages/Messages";
import Auth from "@/pages/Auth";
import ResetPassword from "@/pages/ResetPassword";
import Profile from "@/pages/Profile";
import LocationPage from "@/pages/LocationPage";
import StatePage from "@/pages/StatePage";
import CityPage from "@/pages/CityPage";
import Sitemap from "@/pages/Sitemap";
import MyProfile from "@/pages/MyProfile";
import HowItWorks from "@/pages/HowItWorks";
import TermsOfService from "@/pages/TermsOfService";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import AdminPanel from "@/pages/AdminPanel";
import DisputeResolution from "@/pages/DisputeResolution";
import PlatformFees from "@/pages/PlatformFees";
import AboutPage from "@/pages/AboutPage";
import PaymentSuccess from "@/pages/PaymentSuccess";
import HelpCenterPage from "@/pages/ContactPage";
import ContactPage from "@/pages/ContactPageNew";
import NotificationsPage from "@/pages/NotificationsPage";
import TrustAndSafety from "@/pages/TrustAndSafety";
import AdvertisePage from "@/pages/AdvertisePage";
import VerifyEmail from "@/pages/VerifyEmail";
import VerificationBanner from "@/components/VerificationBanner";

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

// Pages that should show footer (public/marketing pages)
const FOOTER_PAGES = ["/", "/browse", "/trust", "/trust-and-safety", "/fees", "/terms-of-service", "/privacy-policy", "/sitemap", "/about", "/contact", "/help-center", "/how-it-works", "/advertise"];

function AppContent() {
  const location = useLocation();
  
  // Show footer on public pages and location pages (SEO pages)
  const isLocationPage = location.pathname.match(/^\/[a-z-]+$/i) && 
    !FOOTER_PAGES.includes(location.pathname) &&
    !["/dashboard", "/create-listing", "/messages", "/auth"].some(p => location.pathname.startsWith(p)) &&
    !location.pathname.startsWith("/items/") &&
    !location.pathname.startsWith("/profile/");
  
  // Also show footer on state/city pages
  const isStateCityPage = location.pathname.match(/^\/[a-z-]+\/[a-z-]+$/i);
  
  const showFooter = FOOTER_PAGES.includes(location.pathname) || isLocationPage || isStateCityPage;
  
  // Internal pages that need vertical centering
  const isInternalPage = !showFooter;

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Paradise Borrowed — Borrow Anything, Anywhere</title>
        <meta name="description" content="Paradise Borrowed is a peer-to-peer borrowing marketplace. Borrow gear, equipment, vehicles, and more from people in your community or vacation destination." />
        <meta property="og:title" content="Paradise Borrowed — Borrow Anything, Anywhere" />
        <meta property="og:description" content="Find and borrow items from people near you. Save money, earn money, reduce waste." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href={`https://www.paradiseborrowed.com${location.pathname}`} />
      </Helmet>
      <ScrollToTop />
      <Navbar />
      <VerificationBanner />
      <main className={`flex-1 ${isInternalPage ? "flex flex-col justify-center" : ""}`}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/browse" element={<Browse />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/sitemap" element={<Sitemap />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/trust" element={<DisputeResolution />} />
          <Route path="/trust-and-safety" element={<TrustAndSafety />} />
          <Route path="/fees" element={<PlatformFees />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/help-center" element={<HelpCenterPage />} />
          <Route path="/advertise" element={<AdvertisePage />} />
          <Route path="/items/:id" element={<ItemDetail />} />
          <Route path="/create-listing" element={<CreateListing />} />
          <Route path="/edit-listing/:itemId" element={<EditListing />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/messages" element={<Messages />} />
          <Route path="/messages/:conversationId" element={<Messages />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/my-profile" element={<MyProfile />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/admin" element={<AdminPanel />} />
          <Route path="/profile/:userId" element={<Profile />} />
          {/* SEO-friendly state/city routes */}
          <Route path="/:stateSlug/:citySlug" element={<CityPage />} />
          {/* SEO-friendly location routes (Caribbean & state pages) */}
          <Route path="/:locationSlug" element={<LocationPage />} />
        </Routes>
      </main>
      {showFooter && <Footer />}
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppContent />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
